import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  forwardRef,
  Input,
  inject,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MatNativeDateModule,
} from '@angular/material/core';
import { MatCalendar, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatActionList, MatListItem } from '@angular/material/list';
import {
  ISO_DATE_FORMATS,
  IsoDateAdapter,
} from '../stena-date-picker/stena-date-picker.component';

export interface StenaDateRangeValue {
  fromDate: Date | null;
  toDate: Date | null;
}

function noop(): void {
  return;
}

export type PreDefinedDateOptions = {
  id: number;
  label: string;
};

export interface PreDefinedDateSelectionEvent {
  id: number;
}

/** @title Date range picker forms integration */
@Component({
  selector: 'stena-daterange-picker',
  templateUrl: 'stena-daterange-picker.component.html',
  styleUrl: 'stena-daterange-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StenaDateRangePickerComponent),
      multi: true,
    },
    {
      provide: DateAdapter,
      useClass: IsoDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: ISO_DATE_FORMATS,
    },
  ],
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    MatListItem,
    MatActionList,
    OverlayModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class StenaDateRangePickerComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private onChange: (value: StenaDateRangeValue | null) => void = noop;
  private onTouched: () => void = noop;

  isPickerOpen = false;
  private draftStart: Date | null = null;
  private draftEnd: Date | null = null;

  @Input({
    transform: (value: Date | null | undefined): Date | null => value || null,
  })
  startDate: Date | null = null;
  @Input({
    transform: (value: Date | null | undefined): Date | null => value || null,
  })
  endDate: Date | null = null;
  @Input() label = '';
  @Input() showHint = true;
  @Input() preDefinedDatesOptions: PreDefinedDateOptions[] = [];
  @Output() handleDateOptionChange: EventEmitter<StenaDateRangeValue | null> =
    new EventEmitter();
  @Output() predefinedDateSelected =
    new EventEmitter<PreDefinedDateSelectionEvent>();
  @ViewChild(MatCalendar) private calendar?: MatCalendar<Date>;

  readonly range = new FormGroup({
    fromDate: new FormControl<Date | null>(this.draftStart),
    toDate: new FormControl<Date | null>(this.draftEnd),
  });

  constructor() {
    this.range.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.onChange({
          fromDate: value.fromDate ?? null,
          toDate: value.toDate ?? null,
        });
      });
  }

  private normalizeDate(date: Date): number {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    ).getTime();
  }

  readonly dateClass = (date: Date): string => {
    const fromDate = this.draftStart ?? this.range.value.fromDate;
    const toDate = this.draftEnd ?? this.range.value.toDate;

    if (!fromDate && !toDate) {
      return '';
    }

    const current = this.normalizeDate(date);
    const startValue = fromDate ? this.normalizeDate(fromDate) : null;
    const endValue = toDate ? this.normalizeDate(toDate) : null;

    if (startValue !== null && current === startValue) {
      return 'stena-range-start';
    }

    if (endValue !== null && current === endValue) {
      return 'stena-range-end';
    }

    if (
      startValue !== null &&
      endValue !== null &&
      current > Math.min(startValue, endValue) &&
      current < Math.max(startValue, endValue)
    ) {
      return 'stena-range-between';
    }

    return '';
  };

  get overlayOffsetX(): number {
    return this.preDefinedDatesOptions.length > 0 ? 270 : 36;
  }

  get overlayPositions(): ConnectedPosition[] {
    return [
      {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
        offsetX: this.overlayOffsetX,
        offsetY: 8,
      },
      {
        originX: 'end',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
        offsetX: this.overlayOffsetX,
        offsetY: -8,
      },
    ];
  }

  get popupClassName(): string {
    return this.preDefinedDatesOptions.length > 0
      ? 'stena-range-popup-with-options'
      : 'stena-range-popup-without-options';
  }

  get calendarAnchorDate(): Date | null {
    if (this.draftStart && !this.draftEnd) {
      return null;
    }

    return (
      this.draftEnd ??
      this.draftStart ??
      this.range.value.toDate ??
      this.range.value.fromDate ??
      new Date()
    );
  }

  togglePopup(): void {
    if (this.isPickerOpen) {
      this.closePopup();
      return;
    }

    this.openPopup();
  }

  openPopup(): void {
    this.draftStart = this.range.value.fromDate ?? this.startDate ?? null;
    this.draftEnd = this.range.value.toDate ?? this.endDate ?? null;
    this.isPickerOpen = true;
    queueMicrotask(() => this.refreshCalendarClasses());
  }

  closePopup(): void {
    this.isPickerOpen = false;
    this.markAsTouched();
  }

  clearRange(): void {
    this.draftStart = null;
    this.draftEnd = null;
    this.range.setValue({ fromDate: null, toDate: null });
    this.refreshCalendarClasses();
  }

  onCalendarDateSelected(date: Date | null): void {
    if (!date) {
      return;
    }

    if (!this.draftStart || this.draftEnd) {
      this.draftStart = date;
      this.draftEnd = null;
    } else {
      this.draftEnd = date;
      if (this.draftEnd && this.draftEnd < this.draftStart) {
        const tmp = this.draftStart;
        this.draftStart = this.draftEnd;
        this.draftEnd = tmp;
      }
    }

    this.range.setValue({
      fromDate: this.draftStart,
      toDate: this.draftEnd,
    });

    this.refreshCalendarClasses();
  }

  writeValue(value: StenaDateRangeValue | null): void {
    this.draftStart = value?.fromDate ? new Date(value.fromDate) : null;
    this.draftEnd = value?.toDate ? new Date(value.toDate) : null;
    this.range.setValue(
      {
        fromDate: this.draftStart,
        toDate: this.draftEnd,
      },
      { emitEvent: false }
    );
    this.refreshCalendarClasses();
  }

  registerOnChange(fn: (value: StenaDateRangeValue | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.range.disable({ emitEvent: false });
      return;
    }

    this.range.enable({ emitEvent: false });
  }

  markAsTouched(): void {
    this.onTouched();
  }

  protected selectPredefinedDate(dateOptionId: number): void {
    this.predefinedDateSelected.emit({
      id: dateOptionId,
    });

    this.togglePopup();
  }

  protected refreshCalendarClasses(): void {
    this.cdr.detectChanges();
    this.calendar?.updateTodaysDate();
  }
}
